// Logs any missing NEXT_PUBLIC_* frontend env vars once, in the browser, on startup.
// NEXT_PUBLIC_* values are inlined at BUILD time, so each must be referenced explicitly
// here (you cannot enumerate process.env for NEXT_PUBLIC_* at runtime in the browser).

const FRONTEND_ENV: Record<string, string | undefined> = {
  NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
  NEXT_PUBLIC_ANALYTICS_API_URL: process.env.NEXT_PUBLIC_ANALYTICS_API_URL,
  NEXT_PUBLIC_ANALYTICS_ENABLED: process.env.NEXT_PUBLIC_ANALYTICS_ENABLED,
  NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
  NEXT_PUBLIC_AUTO_SUBMIT_ENABLED: process.env.NEXT_PUBLIC_AUTO_SUBMIT_ENABLED,
  NEXT_PUBLIC_BASE_PATH: process.env.NEXT_PUBLIC_BASE_PATH,
  NEXT_PUBLIC_COURSES_API_BASE_URL: process.env.NEXT_PUBLIC_COURSES_API_BASE_URL,
  NEXT_PUBLIC_COURSE_DOMAIN_WHITELIST: process.env.NEXT_PUBLIC_COURSE_DOMAIN_WHITELIST,
  NEXT_PUBLIC_COURSE_EMAIL_WHITELIST: process.env.NEXT_PUBLIC_COURSE_EMAIL_WHITELIST,
  NEXT_PUBLIC_COURSE_WHITELIST_ENABLED: process.env.NEXT_PUBLIC_COURSE_WHITELIST_ENABLED,
  NEXT_PUBLIC_DEBUG: process.env.NEXT_PUBLIC_DEBUG,
  NEXT_PUBLIC_CDN_RESOURCES_URL: process.env.NEXT_PUBLIC_CDN_RESOURCES_URL,
  NEXT_PUBLIC_ENABLE_ENTERPRISE_LOGIN: process.env.NEXT_PUBLIC_ENABLE_ENTERPRISE_LOGIN,
  NEXT_PUBLIC_ENABLE_INTERVIEW_FEEDBACK_ASYNC_PIPELINE: process.env.NEXT_PUBLIC_ENABLE_INTERVIEW_FEEDBACK_ASYNC_PIPELINE,
  NEXT_PUBLIC_TF_VIDEO_INTERVIEW_ENABLED: process.env.NEXT_PUBLIC_TF_VIDEO_INTERVIEW_ENABLED,
  NEXT_PUBLIC_TF_ENABLE__VIDEO_INTERVIEW_FOR_USER: process.env.NEXT_PUBLIC_TF_ENABLE__VIDEO_INTERVIEW_FOR_USER,
  NEXT_PUBLIC_PROCTOR_NO_FACE_THRESHOLD_SECONDS: process.env.NEXT_PUBLIC_PROCTOR_NO_FACE_THRESHOLD_SECONDS,
  NEXT_PUBLIC_ENABLE_JD_VIDEO_TEST_MODE: process.env.NEXT_PUBLIC_ENABLE_JD_VIDEO_TEST_MODE,
  NEXT_PUBLIC_ENABLE_JOB_MONITOR: process.env.NEXT_PUBLIC_ENABLE_JOB_MONITOR,
  NEXT_PUBLIC_ENABLE_RESUME_TEST_MODE: process.env.NEXT_PUBLIC_ENABLE_RESUME_TEST_MODE,
  NEXT_PUBLIC_ENABLE_SYSTEM_DESIGN_INTERVIEW: process.env.NEXT_PUBLIC_ENABLE_SYSTEM_DESIGN_INTERVIEW,
  NEXT_PUBLIC_ENABLE_TEMPLATE_DEMO_MODE: process.env.NEXT_PUBLIC_ENABLE_TEMPLATE_DEMO_MODE,
  NEXT_PUBLIC_GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
  NEXT_PUBLIC_GOOGLE_ADS_ID: process.env.NEXT_PUBLIC_GOOGLE_ADS_ID,
  NEXT_PUBLIC_GITHUB_CLIENT_ID: process.env.NEXT_PUBLIC_GITHUB_CLIENT_ID,
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_GTM_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID,
  NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
  NEXT_PUBLIC_LOCAL_ENV: process.env.NEXT_PUBLIC_LOCAL_ENV,
  NEXT_PUBLIC_MICROSOFT_AUTHORITY: process.env.NEXT_PUBLIC_MICROSOFT_AUTHORITY,
  NEXT_PUBLIC_MICROSOFT_CLIENT_ID: process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID,
  NEXT_PUBLIC_MICROSOFT_DOMAIN_HINT: process.env.NEXT_PUBLIC_MICROSOFT_DOMAIN_HINT,
  NEXT_PUBLIC_MICROSOFT_REDIRECT_URI: process.env.NEXT_PUBLIC_MICROSOFT_REDIRECT_URI,
  NEXT_PUBLIC_MICROSOFT_TENANT_ID: process.env.NEXT_PUBLIC_MICROSOFT_TENANT_ID,
  NEXT_PUBLIC_NEXT_QUESTION_POST_FALLBACK: process.env.NEXT_PUBLIC_NEXT_QUESTION_POST_FALLBACK,
  NEXT_PUBLIC_PAYMENTS_ENABLED: process.env.NEXT_PUBLIC_PAYMENTS_ENABLED,
  NEXT_PUBLIC_SHOW_INTERVIEW_TIMER: process.env.NEXT_PUBLIC_SHOW_INTERVIEW_TIMER,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_SUPPORT_AI_ENABLED: process.env.NEXT_PUBLIC_SUPPORT_AI_ENABLED,
  NEXT_PUBLIC_SUPPORT_FEATURE_ENABLED: process.env.NEXT_PUBLIC_SUPPORT_FEATURE_ENABLED,
  NEXT_PUBLIC_TERMS_URL: process.env.NEXT_PUBLIC_TERMS_URL,
  NEXT_PUBLIC_TF_ENABLE_COMBO_PLAN: process.env.NEXT_PUBLIC_TF_ENABLE_COMBO_PLAN,
  NEXT_PUBLIC_TTS_QUESTION_PLAYBACK_RATE: process.env.NEXT_PUBLIC_TTS_QUESTION_PLAYBACK_RATE,
  NEXT_PUBLIC_TURN_PASSWORD: process.env.NEXT_PUBLIC_TURN_PASSWORD,
  NEXT_PUBLIC_TURN_SERVER_URLS: process.env.NEXT_PUBLIC_TURN_SERVER_URLS,
  NEXT_PUBLIC_TURN_USERNAME: process.env.NEXT_PUBLIC_TURN_USERNAME,
  NEXT_PUBLIC_USE_STREAMING_FOR_CODE_QUESTIONS: process.env.NEXT_PUBLIC_USE_STREAMING_FOR_CODE_QUESTIONS,
  NEXT_PUBLIC_WEBINAR_BACKEND_URL: process.env.NEXT_PUBLIC_WEBINAR_BACKEND_URL,
  NEXT_PUBLIC_WEBINAR_WS_URL: process.env.NEXT_PUBLIC_WEBINAR_WS_URL,
};

let alreadyLogged = false;

// `console.warn` survives the production `removeConsole` pass on purpose (see next.config.ts),
// so an always-on check here means every deployed page load carries a 15-line warning about
// build-time vars a visitor cannot fix. A missing NEXT_PUBLIC_* on stage/prod is a deployment
// manifest fault — it belongs in the pipeline, not in the console of a visitor.
// Diagnostics therefore run in development, and in a deployed build only when explicitly
// switched on with NEXT_PUBLIC_DEBUG=true.
function shouldReportMissingEnv(): boolean {
  if (process.env.NODE_ENV !== "production") return true;
  // Same flag and same comparison as `DEBUG_ENABLED` in lib/config.ts.
  return String(process.env.NEXT_PUBLIC_DEBUG ?? "").trim() === "true";
}

/** Log which frontend env vars are missing/empty. Runs once, browser only. */
export function logMissingFrontendEnv(): void {
  if (alreadyLogged || typeof window === "undefined") return;
  if (!shouldReportMissingEnv()) return;
  alreadyLogged = true;

  const missing = Object.entries(FRONTEND_ENV)
    .filter(([, value]) => value === undefined || String(value).trim() === "")
    .map(([name]) => name);

  if (missing.length > 0) {
    console.warn(
      `[ENV] Missing/empty frontend env vars (${missing.length}):\n  - ${missing.join("\n  - ")}`,
    );
  } else {
    console.info("[ENV] All expected NEXT_PUBLIC_* frontend env vars are set.");
  }
}
